import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { candidatesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  screening_pending:      'bg-slate-100 text-slate-700',
  screening_in_progress:  'bg-yellow-100 text-yellow-800',
  hr_docs_pending:        'bg-orange-100 text-orange-800',
  docs_submitted:         'bg-blue-100 text-blue-800',
  docs_under_review:      'bg-blue-100 text-blue-800',
  ready_for_admin_review: 'bg-indigo-100 text-indigo-800',
  offer_pending:          'bg-purple-100 text-purple-800',
  offer_approved:         'bg-violet-100 text-violet-800',
  offer_sent:             'bg-purple-100 text-purple-800',
  offer_accepted:         'bg-emerald-100 text-emerald-800',
  offer_declined:         'bg-red-100 text-red-800',
  day1_scheduled:         'bg-teal-100 text-teal-800',
  onboarding_complete:    'bg-teal-100 text-teal-800',
  i9_pending:             'bg-orange-100 text-orange-800',
  i9_employee_complete:   'bg-blue-100 text-blue-800',
  i9_hr_complete:         'bg-indigo-100 text-indigo-800',
  i9_approved:            'bg-green-100 text-green-800',
  converted_to_employee:  'bg-teal-100 text-teal-800',
  rejected:               'bg-red-100 text-red-800',
  on_hold:                'bg-gray-100 text-gray-700',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'screening_pending', label: 'Screening Pending' },
  { value: 'hr_docs_pending', label: 'HR Docs Pending' },
  { value: 'docs_submitted', label: 'Docs Submitted' },
  { value: 'ready_for_admin_review', label: 'Ready for Review' },
  { value: 'offer_pending', label: 'Offer Pending' },
  { value: 'offer_sent', label: 'Offer Sent' },
  { value: 'offer_accepted', label: 'Offer Accepted' },
  { value: 'day1_scheduled', label: 'Onboarding Scheduled' },
  { value: 'i9_pending', label: 'I-9 Pending' },
  { value: 'i9_approved', label: 'I-9 Approved' },
  { value: 'converted_to_employee', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'on_hold', label: 'On Hold' },
];

// Roles that can create candidates
const CAN_CREATE = ['super_admin', 'compliance_admin', 'hr', 'recruitment'];

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || 'bg-slate-100 text-slate-700';
  const label = status ? status.replace(/_/g, ' ') : 'unknown';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${color}`}>
      {label}
    </span>
  );
}

export default function CandidateList() {
  const navigate              = useNavigate();
  const { admin }             = useAuth();
  const role                  = admin?.role || '';
  const [candidates, setCandidates] = useState([]);
  const [pipeline, setPipeline]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('');
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: 20 };
      if (search)       params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await candidatesAPI.list(params);
      // API returns: { success, message, data: { candidates: [], total_pages, total } }
      const d = res.data?.data || res.data || {};
      setCandidates(Array.isArray(d.candidates) ? d.candidates : []);
      setTotalPages(d.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load candidates.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  useEffect(() => {
    candidatesAPI.pipelineReport()
      .then((res) => {
        const d = res.data?.data || {};
        setPipeline({ summary: d.summary || {}, by_status: d.by_status || {} });
      })
      .catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const stats = [
    { label: 'Total',            value: pipeline?.summary?.total            ?? '—', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Screening',        value: pipeline?.by_status?.screening_pending ?? '—', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    { label: 'Docs Pending',     value: pipeline?.by_status?.hr_docs_pending ?? '—', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    { label: 'Ready for Review', value: pipeline?.by_status?.ready_for_admin_review ?? '—', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    { label: 'Offer Pending',    value: pipeline?.by_status?.offer_pending   ?? '—', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    { label: 'Converted',        value: pipeline?.summary?.converted         ?? '—', color: 'bg-teal-50 text-teal-700 border-teal-200' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">New Bench</h1>
          <p className="text-slate-500 text-sm mt-1">Hiring pipeline — from application to onboarding.</p>
        </div>
        {CAN_CREATE.includes(role) && (
          <Link
            to="/candidates/create"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            + Add New Bench
          </Link>
        )}
      </div>

      {/* Pipeline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 border border-slate-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-slate-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="font-medium">No bench entries found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Visa</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Location</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Created</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {candidates.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">{c.first_name} {c.last_name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.email}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                        {c.visa_type || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.current_location || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 text-slate-500">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/candidates/${c.id}`)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40 hover:bg-slate-50"
          >Previous</button>
          <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40 hover:bg-slate-50"
          >Next</button>
        </div>
      )}
    </div>
  );
}
